<template>
  <AppSection
    id="email-templates-page"
    :title="$getConst.pages.email_template_configuration.email_template_page_title"
    type="section"
    select-language
  >
      <AppSection
        id="email-templates-type"
        type="sub-section-1"
        :title="templatesTypeTitle"
      >


      <b-overlay :show="isLoading || isSubmitting">
        <template #overlay>
          <AppSpinner size="5" class="text-secondary"/>
        </template>

        <b-row>
          <b-col>
            <EmailTemplateSlide :template-names="templateNames" @onSelect="getTemplates"/>
          </b-col>
        </b-row>

        <hr class="w-75 mb-4"/>

        <b-row>

          <b-col v-if="!templates.length && !isLoading">
            <h4 class="text-center">{{$t($getConst.pages.email_template_configuration.no_template_selected_message) | capitalize}}</h4>
          </b-col>

          <template v-else>
            <b-col md="6">
              <b-form-group id="template-language" :label="$t($getConst.pages.email_template_configuration.template_language_label)" label-class="my-auto" label-cols="4" label-for="template-language-input">
                <multiselect
                  class="border-right-0 border-left-0"
                  v-model="language"
                  track-by="id"
                  label="name"
                  :placeholder="$t(this.$getConst.pages.form.select_option)"
                  :options="languages"
                  :disabled="isSubmitting || isLoading"
                  :allowEmpty="false"
                  @select="selectTemplate"
                >
                  <template slot="singleLabel" slot-scope="props">
                    <lang-flag :iso="props.option.code" :squared="false" class="mr-2"/>
                    {{$t(props.option.name) | capitalize}}
                  </template>
                  <template slot="option" slot-scope="props">
                    <lang-flag :iso="props.option.code" :squared="false" class="mr-2"/>
                    {{$t(props.option.name.toLowerCase()) | capitalize}}
                  </template>
                </multiselect>
              </b-form-group>
              <hr/>
              <b-form-group id="refer" :label="$t($getConst.pages.email_template_configuration.email_refer_label) + ' *'" label-for="refer-input">
                <b-form-input
                  id="refer-input"
                  v-model.trim="$v.emailTemplateForm.refer.$model"
                  :class="{ 'is-invalid': $v.emailTemplateForm.refer.$error }"
                  :placeholder="$t($getConst.pages.email_template_configuration.email_refer_placeholder) + ' *'"
                  size="lg"
                  required
                  disabled
                ></b-form-input>
                <AppFormFeedback :errors="$v.emailTemplateForm.refer"/>
              </b-form-group>
              <b-form-group id="subject" :label="$t($getConst.pages.email_template_configuration.email_subject_label) + ' *'" label-for="subject-input">
                <b-form-input
                  id="subject-input"
                  v-model.trim="$v.emailTemplateForm.subject.$model"
                  :class="{ 'is-invalid': $v.emailTemplateForm.subject.$error }"
                  :placeholder="$t($getConst.pages.email_template_configuration.email_subject_placeholder) + ' *'"
                  size="lg"
                  required
                ></b-form-input>
                <AppFormFeedback :errors="$v.emailTemplateForm.subject"/>
              </b-form-group>

              <hr/>

              <b-form-group id="title" :label="$t($getConst.pages.email_template_configuration.email_title_label) + ' *'" label-for="title-input">
                <b-form-input
                  id="title-input"
                  v-model.trim="$v.emailTemplateForm.title.$model"
                  :class="{ 'is-invalid': $v.emailTemplateForm.title.$error }"
                  :placeholder="$t($getConst.pages.email_template_configuration.email_title_placeholder) + ' *'"
                  size="lg"
                  required
                ></b-form-input>
                <AppFormFeedback :errors="$v.emailTemplateForm.title"/>
              </b-form-group>

              <b-form-group id="subtitle" :label="$t($getConst.pages.email_template_configuration.email_subtitle_label)" label-for="subtitle-input">
                <b-form-input
                  id="subtitle-input"
                  v-model.trim="$v.emailTemplateForm.subtitle.$model"
                  :class="{ 'is-invalid': $v.emailTemplateForm.subtitle.$error }"
                  :placeholder="$t($getConst.pages.email_template_configuration.email_subtitle_placeholder)"
                  size="lg"
                  required
                ></b-form-input>
                <AppFormFeedback :errors="$v.emailTemplateForm.subtitle"/>
              </b-form-group>
              <b-form-group id="introduction" :label="$t($getConst.pages.email_template_configuration.email_introduction_label) + ' *'" label-for="introduction-input">
                <vue-editor
                  id="introduction-input"
                  ref="introduction"
                  v-if="!isLoading"
                  :placeholder="$t($getConst.pages.email_template_configuration.email_introduction_placeholder) + ' *'"
                  :disabled="isSubmitting"
                  :editorToolbar="toolbarOptions"
                  v-model.trim="$v.emailTemplateForm.introduction.$model"
                  :class="{ 'is-invalid': $v.emailTemplateForm.introduction.$error }"
                >
                  <template #toolbar>
                    <div class="ql-toolbar ql-snow">
                      <button disabled class="custom-disabled">{{$t($getConst.pages.email_template_configuration.toolbar_variables)}}: </button>

                      <button
                        v-for="variable in variables"
                        @click="addVariable('introduction', variable.value)"
                        type="button"
                        class="w-auto border-right"
                      >
                        {{variable.label}}
                      </button>
                    </div>
                  </template>
                </vue-editor>
                <AppFormFeedback :errors="$v.emailTemplateForm.introduction"/>
              </b-form-group>
              <b-form-group id="button-description" :label="$t($getConst.pages.email_template_configuration.email_button_description_label)" label-for="button-description-input">
                <vue-editor
                  id="button-description-input"
                  ref="buttonDescription"
                  v-if="!isLoading"
                  :placeholder="$t($getConst.pages.email_template_configuration.email_button_description_placeholder)"
                  :disabled="isSubmitting"
                  :editorToolbar="toolbarOptions"
                  v-model.trim="$v.emailTemplateForm.button_description.$model"
                  :class="{ 'is-invalid': $v.emailTemplateForm.button_description.$error }"
                >
                  <template #toolbar>
                    <div class="ql-toolbar ql-snow">
                      <button disabled class="custom-disabled">{{$t($getConst.pages.email_template_configuration.toolbar_variables)}}: </button>

                      <button
                        v-for="variable in variables"
                        @click="addVariable('buttonDescription', variable.value)"
                        type="button"
                        class="border-right w-auto"
                      >
                        {{variable.label}}
                      </button>
                    </div>
                  </template>
                </vue-editor>
                <AppFormFeedback :errors="$v.emailTemplateForm.button_description"/>
              </b-form-group>

              <template v-for="(value, button_type) in buttons_enabled">
                <div v-if="value.exist" class="form-group">
                  <div class="d-flex justify-content-between">
                    <label>{{$t($getConst.pages.email_template_configuration['email_button_'+button_type])}}</label>
                    <b-form-checkbox class="d-inline" switch v-model="buttons_enabled[button_type].enabled" size="lg"></b-form-checkbox>
                  </div>
                  <div v-if="value.enabled" class="d-flex btn-inputs" :class="{ 'is-invalid': $v.emailTemplateForm['button_'+button_type+'_label'].$error }">
                    <input
                      type="color"
                      v-model.trim="$v.emailTemplateForm['button_'+button_type+'_color'].$model"
                      class="form-control-lg form-control-color mr-2"
                    >
                    <b-form-input
                      :id="'button-'+button_type+'-input'"
                      v-model.trim="$v.emailTemplateForm['button_'+button_type+'_label'].$model"
                      class="flex-grow-1"
                      :class="{ 'is-invalid': $v.emailTemplateForm['button_'+button_type+'_label'].$error }"
                      :placeholder="$t($getConst.pages.email_template_configuration['email_button_'+button_type+'_label_placeholder'])"
                      size="lg"
                    ></b-form-input>
                  </div>
                  <AppFormFeedback :errors="$v.emailTemplateForm['button_'+button_type+'_label']"/>
                </div>
              </template>

            <b-form-group id="ending" :label="$t($getConst.pages.email_template_configuration.email_ending_label) + ' *'" label-for="ending-input">
                <vue-editor
                  id="ending-input"
                  ref="ending"
                  v-if="!isLoading"
                  :placeholder="$t($getConst.pages.email_template_configuration.email_ending_placeholder) + ' *'"
                  :disabled="isSubmitting"
                  :editorToolbar="toolbarOptions"
                  v-model.trim="$v.emailTemplateForm.ending.$model"
                  :class="{ 'is-invalid': $v.emailTemplateForm.ending.$error }"
                >
                  <template #toolbar>
                    <div class="ql-toolbar ql-snow">
                      <button disabled class="custom-disabled">{{$t($getConst.pages.email_template_configuration.toolbar_variables)}}: </button>

                      <button
                        v-for="variable in variables"
                        @click="addVariable('ending', variable.value)"
                        type="button"
                        class="w-auto border-right"
                      >
                        {{variable.label}}
                      </button>
                    </div>
                  </template>
                </vue-editor>
                <AppFormFeedback :errors="$v.emailTemplateForm.ending"/>
              </b-form-group>

            <Attachments
              v-if="!isLoading"
              :scope-id="this.scope_id"
              :entity-type="this.emailTemplateEntity"
              :entity-id="this.emailTemplateForm.id"
              :file-infos="this.emailTemplateForm.attachments"
              :label=$t($getConst.pages.email_template_configuration.attachments)
            />
          </b-col>

            <b-col md="6">
              <EmailTemplateOverview
                :display-table="
                  templateType !== this.$getConst.pages.general_email_templates_names.account_creation &&
                  templateType !== this.$getConst.pages.general_email_templates_names.email_confirmation &&
                  templateType !== this.$getConst.pages.timesheets_email_templates_names.timesheet_rejected &&
                  (!templateTypeIsInvoice)
                "
                :display-comment="
                  templateType === this.$getConst.pages.timesheets_email_templates_names.timesheet_rejected
                "
                :display-invoice-table="templateTypeIsInvoice"
                :email-template-form="emailTemplateForm"
                :language=language
              />
            </b-col>
          </template>

        </b-row>
        <AppConfirmButtons
          v-if="!isLoading && templates.length"
          no-cancel
          :first-button="hasPermission(this.$getConst.permissions.writeEmailTemplate)"
          :second-button="hasPermission(this.$getConst.permissions.readEmailTemplate)"
          :second-button-text="this.$getConst.pages.email_template_configuration.test_email_button"
          :is-loading="isSubmitting"
          @onSubmit="save"
          @onSecondSubmit="testTemplate"
          :is-disabled="$v.emailTemplateForm.$invalid"
        />
      </b-overlay>
    </AppSection>
  </AppSection>
</template>

<script>
  import AppSection from "../../../../components/common/sections/AppSection";
  import {required, maxLength} from "vuelidate/lib/validators";
  import {VueEditor} from "vue2-editor";
  import AppConfirmButtons from "../../../../components/common/utils/AppConfirmButtons";
  import AppFormFeedback from "../../../../components/common/form/AppFormFeedback";
  import EmailTemplateOverview from "./emailTemplateOverview";
  import EmailTemplateSlide from "./emailTemplateSlide";
  import {getTemplatesByScopeAndType, sendTestMail, setEmailTemplate} from "../../../../api/emailTemplates.api";
  import AppSpinner from "../../../../components/common/utils/AppSpinner";
  import Attachments from "../../../../components/common/attachments/Attachments";

  export default {
    name: "index",
    components: {
      AppSpinner,
      EmailTemplateSlide,
      EmailTemplateOverview, AppFormFeedback, AppConfirmButtons, AppSection, VueEditor,
      Attachments},
    props: {
      scope_id: null,
      type: null
    },
    data() {
      return {
        toolbarOptions: [
          ['bold', 'italic', 'underline', 'strike'],
          [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
          [{ 'list': 'ordered'}, { 'list': 'bullet' }],
          [{ 'color': ['#000000', '#3c4b64','#002776','#00a0da','#e55353','#80379b','#28a745','#e98300'] }],
          [{ 'background': ['transparent', '#3c4b64','#002776','#00a0da','#e55353','#80379b','#28a745','#e98300'] }],
          ['link']
        ],

        templates: [],
        templateType: null,
        language: null,
        languages: [],
        isLoading: false,
        isSubmitting: false,
        buttons_enabled: {
          review: { exist: true, enabled: true },
          approve: { exist: true, enabled: true },
          reject: { exist: true, enabled: true },
          principal: { exist: true, enabled: true }
        },

        emailTemplateForm: {
          id: null,
          subject: null,
          title: null,
          subtitle: null,
          introduction: null,
          button_description: null,
          button_principal_label: null,
          button_review_label: null,
          button_approve_label: null,
          button_reject_label: null,
          button_principal_color: "#000000",
          button_review_color: "#000000",
          button_approve_color: "#000000",
          button_reject_color: "#000000",
          ending: null,
          refer: null,
          attachments: [],
        },
        emailTemplateEntity: "email-template",

        variables: [],
      }
    },
    validations: {
      emailTemplateForm: {
        subject: {required, maxLength: maxLength(191)},
        title: {required, maxLength: maxLength(191)},
        subtitle: {maxLength: maxLength(191)},
        introduction: {required, maxLength: maxLength(65535)},
        button_description: {maxLength: maxLength(65535)},
        button_principal_label: {maxLength: maxLength(191)},
        button_review_label: {maxLength: maxLength(191)},
        button_approve_label: {maxLength: maxLength(191)},
        button_reject_label: {maxLength: maxLength(191)},
        button_principal_color: {maxLength: maxLength(7)},
        button_review_color: {maxLength: maxLength(7)},
        button_approve_color: {maxLength: maxLength(7)},
        button_reject_color: {maxLength: maxLength(7)},
        ending: {required, maxLength: maxLength(65535)},
        refer: {maxLength: maxLength(100)}
      }
    },
    computed: {
      templateNames() {
        if (this.type === 'general') {
          return this.$getConst.pages.general_email_templates_names;
        } else if (this.type === 'timesheets') {
          return this.$getConst.pages.timesheets_email_templates_names;
        } else if (this.type === 'invoices') {
          return this.$getConst.pages.invoices_email_templates_names;
        }
      },
      templatesTypeTitle() {
        if (this.type === "general") {
          return this.$getConst.pages.email_template.email_templates_general_type;
        } else if(this.type === "timesheets") {
          return this.$getConst.pages.email_template.email_templates_timesheets_type;
        } else if (this.type === "invoices") {
          return this.$getConst.pages.email_template.email_templates_invoices_type;
        }
      },
      templateTypeIsInvoice() {
        return (this.templateType === this.$getConst.pages.invoices_email_templates_names.invoices_creation ||
          this.templateType === this.$getConst.pages.invoices_email_templates_names.invoices_update);
      }
    },
    methods: {
      getTemplates(type) {
        this.templateType = type;
        this.isLoading = true;
        getTemplatesByScopeAndType(this.scope_id, type).then(
          response => {
            this.templates = response.data;
            this.language = response.data[0].language;
            this.emailTemplateForm = response.data[0];
            this.languages = [];
            this.templates.forEach(template => {
              this.languages.push(template.language);
            })
            this.setTemplateButtons();
            this.setEditorVariables();
            this.isLoading = false;
          },
          (error) => {
            this.isLoading = false;
            this.handleApiError(error);
          }
        )
      },
      addVariable(ref, variable) {
        let quill = this.$refs[ref].quill;
        if (quill.getSelection()) {
          let index = quill.getSelection().index
          quill.insertText(index, "{{"+variable+"}} ");
          quill.setSelection(index + (variable.length + 5))
        }
      },
      save() {
        this.isSubmitting = true;
        this.emailTemplateForm.attachments=[];
        setEmailTemplate(this.scope_id, this.emailTemplateForm.id, this.emailTemplateForm).then(
          response => {
            this.emailTemplateForm = response.data;
            this.isSubmitting = false;
            this.$snotify.success(this.$t(this.$getConst.pages.alerts.success_update));
          },
          (error) => {
            this.handleApiError(error);
            this.isSubmitting = false;
          }
        )
      },
      testTemplate() {
        this.isSubmitting = true;
        sendTestMail(this.emailTemplateForm, this.templateType, this.scope_id).then(
          () => {
            this.isSubmitting = false;
            this.$snotify.success(this.$t(this.$getConst.pages.alerts.success_operation));
          },
          (error) => {
            this.handleApiError(error);
            this.isSubmitting = false;
          }
        )
      },
      setEditorVariables() {
        if (this.templateType === this.$getConst.pages.general_email_templates_names.account_creation) {
          this.variables = [
            {label: this.$t(this.$getConst.pages.email_template_configuration.firstName), value: "FirstName"},
            {label: this.$t(this.$getConst.pages.email_template_configuration.lastName), value: "LastName"},
            {label: this.$t(this.$getConst.pages.email_template_configuration.login), value: "Login"},
            {label: this.$t(this.$getConst.pages.email_template_configuration.password), value: "Password"},
            {label: this.$t(this.$getConst.pages.email_template_configuration.scopeHelpdeskEmail), value: "ScopeHelpdeskEmail"},
            {label: this.$t(this.$getConst.pages.email_template_configuration.signInUrl), value: "signInUrl"},
          ];
        } else if (this.templateType === this.$getConst.pages.general_email_templates_names.email_confirmation) {
          this.variables = [
            {label: this.$t(this.$getConst.pages.email_template_configuration.firstName), value: "FirstName"},
            {label: this.$t(this.$getConst.pages.email_template_configuration.lastName), value: "LastName"},
            {label: this.$t(this.$getConst.pages.email_template_configuration.scopeHelpdeskEmail), value: "ScopeHelpdeskEmail"},
            {label: this.$t(this.$getConst.pages.email_template_configuration.confirmationUrl), value: "confirmation_url"},
          ];
        } else if(Object.keys(this.$getConst.pages.timesheets_email_templates_names).find(index => this.templateType === index)){
          this.variables = [
            {label: this.$t(this.$getConst.pages.email_template_configuration.worker_first_name), value: "WorkerFirstName"},
            {label: this.$t(this.$getConst.pages.email_template_configuration.worker_last_name), value: "WorkerLastName"},
            {label: this.$t(this.$getConst.pages.email_template_configuration.company), value: "CompanyName"},
            {label: this.$t(this.$getConst.pages.email_template_configuration.job), value: "JobTitle"},
            {label: this.$t(this.$getConst.pages.email_template_configuration.contract_start_date), value: "ContractStartDate"},
            {label: this.$t(this.$getConst.pages.email_template_configuration.contract_end_date), value: "ContractEndDate"},
            {label: this.$t(this.$getConst.pages.email_template_configuration.timesheet_start_date), value: "TimesheetStartDate"},
            {label: this.$t(this.$getConst.pages.email_template_configuration.timesheet_end_date), value: "TimesheetEndDate"},
            {label: this.$t(this.$getConst.pages.email_template_configuration.approver_name), value: "ApproverName"},
            {label: this.$t(this.$getConst.pages.email_template_configuration.scopeHelpdeskEmail), value: "ScopeHelpdeskEmail"},
          ];
        } else if (this.templateTypeIsInvoice) {
          this.variables = [
            {label: this.$t(this.$getConst.pages.email_template_configuration.contact_first_name), value: "ContactFirstName"},
            {label: this.$t(this.$getConst.pages.email_template_configuration.contact_last_name), value: "ContactLastName"},

            {label: this.$t(this.$getConst.pages.email_template_configuration.worker_full_name_n), value: "WorkerFullName#N"},
            {label: this.$t(this.$getConst.pages.email_template_configuration.date_of_issue_n), value: "DateOfIssue#N"},
            {label: this.$t(this.$getConst.pages.email_template_configuration.due_date_n), value: "DueDate#N"},
            {label: this.$t(this.$getConst.pages.email_template_configuration.total_hours_n), value: "TotalHours#N"},
            {label: this.$t(this.$getConst.pages.email_template_configuration.amount_due_n), value: "AmountDue#N"},
            {label: this.$t(this.$getConst.pages.email_template_configuration.currency_n), value: "Currency#N"},
          ];
        }
      },
      setTemplateButtons() {
        if (
          this.templateType === this.$getConst.pages.timesheets_email_templates_names.pending_timesheet ||
          this.templateType === this.$getConst.pages.timesheets_email_templates_names.pending_timesheet_reminder
        ) {
          this.buttons_enabled = {
            review: { exist: true, enabled: true },
            approve: { exist: true, enabled: true },
            reject: { exist: true, enabled: true },
            principal: { exist: true, enabled: true }
          }
        } else {
          this.buttons_enabled = {
            review: { exist: false, enabled: false },
            approve: { exist: false, enabled: false },
            reject: { exist: false, enabled: false },
            principal: { exist: true, enabled: false }
          }
        }

        this.enableTemplateButtons();
      },
      selectTemplate(language) {
        this.emailTemplateForm = this.templates.find(template => template.language.id === language.id);
        this.enableTemplateButtons();
      },
      enableTemplateButtons() {
        for (let button_type in this.buttons_enabled) {
          this.buttons_enabled[button_type].enabled = this.emailTemplateForm['button_'+button_type+'_label'] !== null;
        }
        this.buttons_enabled.principal.enabled = true;
      }
    },
    watch: {
      buttons_enabled: {
        deep: true,
        handler(newValue, oldValue) {
          for (let button_type in this.buttons_enabled) {
            if (!newValue[button_type].enabled) {
              this.emailTemplateForm['button_'+button_type+'_label'] = null;
              this.emailTemplateForm['button_'+button_type+'_color'] = "#000000";
            }
          }
        }
      }
    }
  }
</script>
<style>
  .custom-disabled {
    width: auto !important;
    pointer-events: none;
    font-weight: bold;
  }
  .ql-editor {
    min-height: 70px !important;
  }
  .btn-inputs {
    transition:  all 1s ease;
  }
</style>
